(function () {
  const TikTokEvents = {
    currency: 'GBP',
    identified: false,
    initialized: false,

    getConfig() {
      const configNode = document.getElementById('tiktok-theme-config');
      if (configNode?.textContent) {
        try {
          return JSON.parse(configNode.textContent);
        } catch (error) {
          console.error('TikTok config parse failed:', error);
        }
      }

      return window.theme?.tiktok || null;
    },

    whenReady(callback) {
      if (typeof window.ttq === 'undefined') return;

      if (typeof window.ttq.ready === 'function') {
        window.ttq.ready(callback);
        return;
      }

      callback();
    },

    normalizeEmail(email) {
      return String(email || '').trim().toLowerCase();
    },

    normalizePhone(phone) {
      let value = String(phone || '').replace(/[^\d+]/g, '');
      if (!value) return '';

      if (value.startsWith('00')) {
        value = `+${value.slice(2)}`;
      } else if (value.startsWith('0')) {
        value = `+44${value.slice(1)}`;
      } else if (!value.startsWith('+')) {
        value = `+${value}`;
      }

      return value;
    },

    async sha256(value) {
      if (!value || !window.crypto?.subtle) return '';
      const buffer = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
      return Array.from(new Uint8Array(buffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    },

    async identify(customer) {
      if (this.identified || !customer) return;

      const payload = {};

      if (customer.email) {
        const hashedEmail = await this.sha256(this.normalizeEmail(customer.email));
        if (hashedEmail) payload.email = hashedEmail;
      }

      if (customer.phone) {
        const hashedPhone = await this.sha256(this.normalizePhone(customer.phone));
        if (hashedPhone) payload.phone_number = hashedPhone;
      }

      if (customer.id) {
        const hashedId = await this.sha256(String(customer.id));
        if (hashedId) payload.external_id = hashedId;
      }

      if (Object.keys(payload).length === 0) return;

      this.whenReady(() => {
        window.ttq.identify(payload);
      });
      this.identified = true;
    },

    buildContents(items) {
      return (items || [])
        .filter(Boolean)
        .map((item) => ({
          content_id: String(item.content_id || item.id || item.variant_id || item.product_id),
          content_type: item.content_type || 'product',
          content_name: item.content_name || item.title || item.product_title || item.name || ''
        }));
    },

    track(eventName, payload) {
      this.whenReady(() => {
        window.ttq.track(eventName, payload);
      });
    },

    normalizeCartLine(item) {
      if (!item) return null;
      if (item.items?.length) {
        return item.items[item.items.length - 1];
      }
      return item;
    },

    trackViewContent(product) {
      if (!product) return;

      this.track('ViewContent', {
        contents: this.buildContents([
          {
            content_id: product.variant_id || product.id,
            content_type: 'product',
            content_name: product.title
          }
        ]),
        value: Number(product.price || 0),
        currency: product.currency || this.currency
      });
    },

    trackAddToCart(item) {
      const line = this.normalizeCartLine(item);
      if (!line) return;

      const unitPrice = Number(line.final_price ?? line.price ?? line.presentment_price ?? 0) / 100;
      const quantity = Number(line.quantity || 1);

      this.track('AddToCart', {
        contents: this.buildContents([
          {
            content_id: line.variant_id || line.id,
            content_type: 'product',
            content_name: line.product_title || line.title
          }
        ]),
        value: unitPrice * quantity,
        currency: line.currency || this.currency
      });
    },

    trackSearch(searchString, contents) {
      const query = String(searchString || '').trim();
      if (!query) return;

      const payload = {
        search_string: query,
        currency: this.currency
      };

      if (contents?.length) {
        payload.contents = this.buildContents(contents);
        payload.value = contents.reduce((total, item) => total + Number(item.price || 0), 0);
      }

      this.track('Search', payload);
    },

    async trackInitiateCheckout() {
      try {
        const response = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) return;

        const cart = await response.json();
        if (!cart.items?.length) return;

        await new Promise((resolve) => {
          this.track('InitiateCheckout', {
            contents: this.buildContents(
              cart.items.map((item) => ({
                content_id: item.variant_id,
                content_type: 'product',
                content_name: item.product_title
              }))
            ),
            value: Number(cart.total_price || 0) / 100,
            currency: cart.currency || this.currency
          });
          window.setTimeout(resolve, 250);
        });
      } catch (error) {
        console.error('TikTok InitiateCheckout tracking failed:', error);
      }
    },

    trackPurchase(order) {
      if (!order?.contents?.length) return;

      this.track('Purchase', {
        contents: this.buildContents(order.contents),
        value: Number(order.value || 0),
        currency: order.currency || this.currency
      });
    },

    bindEvents() {
      if (this.initialized) return;
      this.initialized = true;

      document.querySelectorAll('.site-search-form').forEach((form) => {
        form.addEventListener('submit', () => {
          const query = form.querySelector('[name="q"]')?.value;
          this.trackSearch(query);
        });
      });

      document.addEventListener('click', async (event) => {
        const checkoutTrigger = event.target.closest('[data-tiktok-checkout]');
        if (!checkoutTrigger) return;

        event.preventDefault();
        await this.trackInitiateCheckout();
        window.location.href = checkoutTrigger.dataset.checkoutUrl || '/checkout';
      });

      document.addEventListener('cart:added', (event) => {
        this.trackAddToCart(event.detail?.item);
      });
    },

    async init(config) {
      if (!config) return;

      this.currency = config.currency || this.currency;

      if (config.customer) {
        await this.identify(config.customer);
      }

      if (config.product) {
        this.trackViewContent(config.product);
      }

      if (config.searchQuery) {
        this.trackSearch(config.searchQuery, config.searchResults);
      }

      if (config.purchase) {
        this.trackPurchase(config.purchase);
      }

      this.bindEvents();
    },

    boot() {
      const config = this.getConfig();
      if (config) {
        this.init(config);
      }
    }
  };

  window.TikTokEvents = TikTokEvents;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TikTokEvents.boot());
  } else {
    TikTokEvents.boot();
  }
})();
